import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { getSolapiConnection, saveSolapiConnection, SolapiConnection } from '../../api/solapiConnection';
import Modal from '../common/Modal';
import PageState from '../common/PageState';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../styles/common/Button.styled';
import { ConsoleInput, ConsoleLabel, FieldError, FormGroup } from '../../styles/common/input.styled';
import * as T from '../../styles/common/table.styled';
import { getErrorMessage } from '../../utils/apiMessage';

type Fields = { name: string; apiKey: string; apiSecret: string; webhookSecret: string };
const emptyFields = (): Fields => ({ name: '', apiKey: '', apiSecret: '', webhookSecret: '' });

const StatusLine = styled.div`display: flex; align-items: center; gap: 8px; margin-bottom: 16px;`;
const Help = styled.p`margin: 0 0 16px; color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; line-height: 1.6;`;

export default function SolapiConnectionModal({ onClose }: { onClose: () => void }) {
  const { mode, basicUser, user } = useAuth();
  const canEdit = mode === 'basic' && Boolean((basicUser ?? user)?.permissionList?.includes('SEND_MSG_EDIT'));
  const [connection, setConnection] = useState<SolapiConnection | null>(null);
  const [fields, setFields] = useState<Fields>(emptyFields);
  const [editing, setEditing] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});

  useEffect(() => {
    if (!canEdit) return;
    let active = true;
    setLoading(true); setLoadError(false);
    getSolapiConnection()
      .then(data => {
        if (!active) return;
        const current = data && typeof data.connectionId === 'number' ? data : null;
        setConnection(current);
        setFields({ ...emptyFields(), name: current?.name || '' });
        setEditing(!current);
      })
      .catch(reason => {
        if (!active) return;
        if ((reason as { response?: { status?: number } })?.response?.status === 404) {
          setConnection(null); setFields(emptyFields()); setEditing(true);
        } else setLoadError(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [canEdit, reloadKey]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit || savingRef.current) return;
    const name = fields.name.trim();
    const apiKey = fields.apiKey.trim();
    const apiSecret = fields.apiSecret.trim();
    const webhookSecret = fields.webhookSecret.trim();
    const nextErrors = {
      name: !name ? '연결 이름을 입력해 주세요.' : undefined,
      apiKey: !apiKey ? 'API Key를 입력해 주세요.' : undefined,
      apiSecret: !apiSecret ? 'API Secret을 입력해 주세요.' : undefined,
      webhookSecret: webhookSecret.length > 256 ? 'Webhook Secret은 256자 이내로 입력해 주세요.' : undefined,
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const updated = await saveSolapiConnection({ name, apiKey, apiSecret, ...(webhookSecret && { webhookSecret }) });
      setConnection(updated);
      setFields({ ...emptyFields(), name: updated.name });
      setEditing(false);
      toast.success('SOLAPI 연동 정보를 저장했습니다.');
    } catch (reason) {
      toast.error(getErrorMessage(reason, 'SOLAPI 연동 정보를 저장하지 못했습니다.'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (!canEdit) return null;

  return <Modal title="SOLAPI 연동" onClose={onClose} closeDisabled={saving} historyMode="none" width={520} footer={<>
    <Button type="button" $variant="secondary" disabled={saving} onClick={onClose}>닫기</Button>
    {!loading && !loadError && (editing
      ? <Button type="submit" form="solapi-connection-form" disabled={saving}>{saving ? '저장 중...' : '저장'}</Button>
      : <Button type="button" onClick={() => { setFields({ ...emptyFields(), name: connection?.name || '' }); setErrors({}); setEditing(true); }}>수정</Button>)}
  </>}>
    {loading ? <PageState status="loading" /> : loadError ? <PageState status="error" title="연결 정보를 불러오지 못했습니다." onRetry={() => setReloadKey(key => key + 1)} /> : <>
      <StatusLine><T.Badge $tone={connection ? 'success' : 'warning'}>{connection ? '연결됨' : '연결 필요'}</T.Badge></StatusLine>
      {editing ? <form id="solapi-connection-form" onSubmit={save}>
        {connection && <Help>기존 API Key 접두어: {connection.apiKeyPrefix}. 보안상 자격 증명 원문은 조회되지 않으므로 수정할 때 Key와 Secret을 새로 입력해 주세요.</Help>}
        <FormGroup><ConsoleLabel htmlFor="solapi-name">연결 이름</ConsoleLabel><ConsoleInput id="solapi-name" value={fields.name} maxLength={100} aria-invalid={Boolean(errors.name)} onChange={event => setFields(current => ({ ...current, name: event.target.value }))} />{errors.name && <FieldError role="alert">{errors.name}</FieldError>}</FormGroup>
        <FormGroup><ConsoleLabel htmlFor="solapi-api-key">API Key</ConsoleLabel><ConsoleInput id="solapi-api-key" value={fields.apiKey} autoComplete="off" aria-invalid={Boolean(errors.apiKey)} onChange={event => setFields(current => ({ ...current, apiKey: event.target.value }))} />{errors.apiKey && <FieldError role="alert">{errors.apiKey}</FieldError>}</FormGroup>
        <FormGroup><ConsoleLabel htmlFor="solapi-api-secret">API Secret</ConsoleLabel><ConsoleInput id="solapi-api-secret" type="password" value={fields.apiSecret} autoComplete="new-password" aria-invalid={Boolean(errors.apiSecret)} onChange={event => setFields(current => ({ ...current, apiSecret: event.target.value }))} />{errors.apiSecret && <FieldError role="alert">{errors.apiSecret}</FieldError>}</FormGroup>
        <FormGroup><ConsoleLabel htmlFor="solapi-webhook-secret">Webhook Secret (선택)</ConsoleLabel><ConsoleInput id="solapi-webhook-secret" type="password" value={fields.webhookSecret} autoComplete="new-password" maxLength={256} aria-invalid={Boolean(errors.webhookSecret)} onChange={event => setFields(current => ({ ...current, webhookSecret: event.target.value }))} />{errors.webhookSecret && <FieldError role="alert">{errors.webhookSecret}</FieldError>}</FormGroup>
        <Help>Webhook Secret을 비워두면 기존 웹훅 설정을 변경하지 않습니다.</Help>
      </form> : <>
        <Help>보안상 API Key는 접두어만 표시되며 API Secret 원문은 조회할 수 없습니다.</Help>
        <FormGroup><ConsoleLabel htmlFor="solapi-name">연결 이름</ConsoleLabel><ConsoleInput id="solapi-name" value={connection?.name || ''} readOnly /></FormGroup>
        <FormGroup><ConsoleLabel htmlFor="solapi-api-key">API Key</ConsoleLabel><ConsoleInput id="solapi-api-key" value={connection?.apiKeyPrefix || ''} readOnly /></FormGroup>
        <FormGroup><ConsoleLabel htmlFor="solapi-api-secret">API Secret</ConsoleLabel><ConsoleInput id="solapi-api-secret" value="보안상 표시되지 않습니다" readOnly /></FormGroup>
        <FormGroup><ConsoleLabel htmlFor="solapi-webhook-secret">Webhook Secret</ConsoleLabel><ConsoleInput id="solapi-webhook-secret" value={connection?.webhookConfigured ? '설정됨' : '미설정'} readOnly /></FormGroup>
      </>}
    </>}
  </Modal>;
}
